import { requireAuth } from '@/utils/auth'
import { createFileRoute } from '@tanstack/react-router'
import PageShell from '@/components/Common/PageShell'
import { Button, Stack, PasswordInput, LoadingOverlay, Box } from '@mantine/core'
import { Form, useForm } from '@mantine/form'
import { Suspense } from 'react'
import { RouteLoading } from '@/components/Common/RouteLoader'
import PAGES from '@/meta'
import ErrorState from '@/components/Common/ErrorState'
import { changeOwnPasswordMutation } from '@/api/main'
import { notifications } from '@mantine/notifications'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import i18next, { type TFunction } from 'i18next'

export const Route = createFileRoute('/change-password')({
  beforeLoad: requireAuth,
  component: () => (
    <Suspense fallback={<RouteLoading title={PAGES.changePassword.title} />}>
      <ChangePasswordComponent />
    </Suspense>
  ),
  errorComponent: ({ error }) => (
    <PageShell title={PAGES.changePassword.title} fullWidth>
      <ErrorState title={i18next.t('errorChangingPassword', { ns: 'changePassword' })} message={error.message} />
    </PageShell>
  ),
})

function ChangePasswordComponent() {
  const { t } = useTranslation('changePassword')
  return (
    <PageShell title={t('changePassword')}>
      <ChangePasswordForm />
    </PageShell>
  )
}

const validateStrongPassword = (password: string, t: TFunction): string | null => {
  if (password.length < 8) {
    return t('passwordMinLength', { ns: 'changePassword' })
  }
  if (!/[A-Z]/.test(password)) {
    return t('passwordUppercase', { ns: 'changePassword' })
  }
  if (!/[a-z]/.test(password)) {
    return t('passwordLowercase', { ns: 'changePassword' })
  }
  if (!/[0-9]/.test(password)) {
    return t('passwordNumber', { ns: 'changePassword' })
  }
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    return t('passwordSpecialChar', { ns: 'changePassword' })
  }
  return null
}

const ChangePasswordForm = () => {
  const { t } = useTranslation('changePassword')
  const { mutate: changePassword, isPending } = useMutation({
    ...changeOwnPasswordMutation(),
    onSuccess: () => {
      notifications.show({ message: t('passwordChangedSuccessfully'), color: 'green' })
    },
    onError: () => {
      notifications.show({ message: t('errorChangingPassword'), color: 'red' })
    },
  })

  const form = useForm({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
    validate: {
      currentPassword: (value: string) => (value.length === 0 ? t('currentPasswordRequired') : null),
      newPassword: (value: string, values) => {
        if (value.length === 0) {
          return t('newPasswordRequired')
        }
        if (value === values.currentPassword) {
          return t('passwordsMustBeDifferent')
        }
        return validateStrongPassword(value, t)
      },
      confirmNewPassword: (value: string, values) => {
        if (value.length === 0) {
          return t('pleaseConfirmPassword')
        }
        if (value !== values.newPassword) {
          return t('passwordsDoNotMatch')
        }
        return null
      },
    },
  })

  const handleSubmit = (values: typeof form.values) => {
    if (form.validate().hasErrors) {
      return
    }

    changePassword({
      body: {
        current_password: values.currentPassword,
        new_password: values.newPassword,
      },
    })
  }

  return (
    <Box pos="relative" maw={400}>
      <LoadingOverlay
        visible={isPending}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
        loaderProps={{ size: 'md', type: 'oval' }}
      />
      <Form form={form} onSubmit={handleSubmit}>
        <Stack gap="md">
          <PasswordInput
            label={t('currentPassword')}
            name="currentPassword"
            {...form.getInputProps('currentPassword')}
            maw={400}
            required
            disabled={isPending}
          />
          <PasswordInput
            label={t('newPassword')}
            {...form.getInputProps('newPassword')}
            maw={400}
            required
            description={t('passwordDescription')}
            disabled={isPending}
          />
          <PasswordInput
            label={t('confirmNewPassword')}
            {...form.getInputProps('confirmNewPassword')}
            maw={400}
            required
            disabled={isPending}
          />
          <Button type="submit" styles={{ root: { alignSelf: 'start' } }} loading={isPending} disabled={isPending}>
            {t('changePassword')}
          </Button>
        </Stack>
      </Form>
    </Box>
  )
}
